import React from 'react';
import { motion } from 'framer-motion';
import {
  Sparkles,
  Check,
  TrendingUp,
  TrendingDown,
  Star,
  Mail,
  RefreshCw,
  AlertTriangle,
  CalendarClock,
  Users,
  MapPin,
  Receipt,
  Timer,
  ShieldCheck,
  Zap,
  Globe,
  BarChart3,
  UtensilsCrossed,
} from 'lucide-react';
import { ToastMark, SquareMark, CloverMark, LightspeedMark, GoogleMark, UberEatsMark, DoorDashMark } from './BrandIcons';
import RachelShowcase from './showcases/RachelShowcase';
import AutomationShowcase from './showcases/AutomationShowcase';
import CateringPipelineShowcase from './showcases/CateringPipelineShowcase';
import WeeklyReportShowcase from './showcases/WeeklyReportShowcase';

/* =================================================================
   ServiceShowcases — reusable dark-bento "day in the life" modules.
   Brand identity: black sections, white panels-on-glass, orange
   accents. CSS/motion mocks only (no stock imagery). EN/ES.
   ================================================================= */

const Panel = ({ children, className = '' }) => (
  <div className={`rounded-2xl bg-white/[0.04] border border-white/10 p-5 hover:border-[#F77F11]/40 transition-colors ${className}`}>
    {children}
  </div>
);

const PanelTag = ({ children }) => (
  <p className="text-[10px] tracking-[0.2em] uppercase font-bold text-neutral-500 mb-3">{children}</p>
);

function Shell({ eyebrow, title, body, why, lang, children, testid }) {
  return (
    <section className="py-20 lg:py-24 bg-neutral-950 text-white relative overflow-hidden" data-testid={testid}>
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -top-32 left-[15%] w-[600px] h-[420px] rounded-full blur-3xl opacity-[0.13]"
        style={{ background: 'radial-gradient(circle, #F77F11 0%, transparent 65%)' }}
      />
      <div className="relative max-w-[1180px] mx-auto px-6 lg:px-10">
        <div className="max-w-2xl">
          <p className="kx-eyebrow-dark">{eyebrow}</p>
          <h2 className="mt-3 text-3xl sm:text-4xl font-bold tracking-[-0.02em] leading-tight">{title}</h2>
          {body && <p className="mt-4 text-neutral-400 text-[15.5px] leading-relaxed">{body}</p>}
          {why && (
            <motion.div
              initial={{ opacity: 0, x: -12 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: '-40px' }}
              transition={{ duration: 0.45, delay: 0.15 }}
              className="mt-5 inline-flex items-start gap-2.5 rounded-xl border border-red-400/30 bg-red-500/10 px-4 py-3"
              data-testid={`${testid}-why`}
            >
              <AlertTriangle className="w-4 h-4 text-red-400 shrink-0 mt-0.5" />
              <p className="text-[13px] leading-relaxed text-red-100/90">
                <span className="font-bold tracking-[0.14em] uppercase text-red-300 text-[10.5px] mr-2">
                  {lang === 'es' ? 'Por qué importa' : 'Why it matters'}
                </span>
                {why}
              </p>
            </motion.div>
          )}
        </div>
        <div className="mt-12">{children}</div>
      </div>
    </section>
  );
}

const Stars = ({ n = 5, className = 'w-3.5 h-3.5' }) => (
  <span className="inline-flex items-center gap-[1px]">
    {Array.from({ length: 5 }).map((_, i) => (
      <Star key={i} className={`${className} ${i < n ? 'text-amber-400 fill-amber-400' : 'text-neutral-600'}`} />
    ))}
  </span>
);

const Grow = ({ children, i = 0, className = '' }) => (
  <motion.div
    initial={{ opacity: 0, y: 18 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: '-50px' }}
    transition={{ duration: 0.5, delay: i * 0.1 }}
    className={className}
  >
    {children}
  </motion.div>
);

/* ── ONLINE ORDERING — how we help you sell more ── */
function OnlineOrderingShowcase({ lang }) {
  const es = lang === 'es';
  return (
    <Shell
      eyebrow={es ? 'Así vendes más' : 'How you sell more'}
      title={es ? 'Cada pedido, diseñado para crecer.' : 'Every order, engineered to grow.'}
      body={
        es
          ? 'No solo tomamos pedidos: subimos el ticket con sugerencias inteligentes, eliminamos comisiones y convertimos a cada cliente en un cliente que repite.'
          : "We don't just take orders: we lift the ticket with smart suggestions, remove commissions and turn every guest into a repeat guest."
      }
      testid="showcase-online-ordering"
      lang={lang}
      why={
        es
          ? 'Cada pedido por marketplace te cuesta ~30%. Cada semana sin canal directo es margen regalado a otros.'
          : 'Every marketplace order costs you ~30%. Every week without a direct channel is margin handed to someone else.'
      }
    >
      <div className="grid md:grid-cols-3 gap-5">
        <Grow i={0}>
          <Panel className="h-full">
            <PanelTag>{es ? 'Upsell inteligente' : 'Smart upsell'}</PanelTag>
            <div className="space-y-2 text-[13px]">
              <div className="flex justify-between text-neutral-300"><span>2× Taco al pastor</span><span className="tabular-nums">$25.00</span></div>
              <div className="flex justify-between text-neutral-300"><span>1× Agua de jamaica</span><span className="tabular-nums">$3.75</span></div>
            </div>
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.5, duration: 0.4 }}
              className="mt-3 rounded-xl border border-[#F77F11]/40 bg-[#F77F11]/10 px-3.5 py-2.5"
            >
              <p className="text-[12px] text-white font-semibold flex items-center gap-1.5">
                <Sparkles className="w-3.5 h-3.5 text-[#F77F11]" /> {es ? 'Sugerencia AI: Guacamole +$4.50' : 'AI suggestion: Guacamole +$4.50'}
              </p>
              <p className="mt-0.5 text-[11px] text-neutral-400">{es ? 'Aceptada por el 32% de los clientes' : 'Accepted by 32% of guests'}</p>
            </motion.div>
            <div className="mt-3 pt-3 border-t border-white/10 flex justify-between text-[13.5px] font-bold text-white">
              <span>Total</span><span className="tabular-nums">$33.25</span>
            </div>
            <p className="mt-2 text-[11px] text-emerald-300 flex items-center gap-1"><TrendingUp className="w-3 h-3" /> {es ? 'Ticket medio +9%' : 'Avg. ticket +9%'}</p>
          </Panel>
        </Grow>
        <Grow i={1}>
          <Panel className="h-full">
            <PanelTag>{es ? 'Cero comisiones' : 'Zero commissions'}</PanelTag>
            <div className="space-y-4">
              <div>
                <div className="flex justify-between text-[12px] mb-1.5"><span className="text-neutral-400">Marketplace</span><span className="text-rose-300 font-bold">-30%</span></div>
                <div className="h-2.5 rounded-full bg-white/10 overflow-hidden">
                  <motion.div initial={{ width: 0 }} whileInView={{ width: '100%' }} viewport={{ once: true }} transition={{ duration: 0.8 }} className="h-full bg-rose-400/70 rounded-full" />
                </div>
              </div>
              <div>
                <div className="flex justify-between text-[12px] mb-1.5"><span className="text-neutral-400">{es ? 'Pedido directo Kitxens' : 'Kitxens direct ordering'}</span><span className="text-[#F77F11] font-bold">0%</span></div>
                <div className="h-2.5 rounded-full bg-white/10 overflow-hidden">
                  <motion.div initial={{ width: 0 }} whileInView={{ width: '8%' }} viewport={{ once: true }} transition={{ duration: 0.8, delay: 0.2 }} className="h-full bg-gradient-to-r from-[#F77F11] to-[#ffb077] rounded-full" />
                </div>
              </div>
            </div>
            <p className="mt-5 text-[24px] font-bold text-white tabular-nums">$3,600<span className="text-[13px] text-neutral-400 font-medium">/{es ? 'año recuperados' : 'yr recovered'}</span></p>
            <p className="mt-1 text-[11px] text-neutral-500">{es ? 'Restaurante medio con 40 pedidos/semana' : 'Average restaurant at 40 orders/week'}</p>
          </Panel>
        </Grow>
        <Grow i={2}>
          <Panel className="h-full">
            <PanelTag>{es ? 'El cliente vuelve' : 'Guests come back'}</PanelTag>
            <div className="space-y-3">
              {[
                { icon: Mail, t: es ? 'Email capturado en el checkout' : 'Email captured at checkout' },
                { icon: CalendarClock, t: es ? 'Campaña automática al día 7' : 'Automated campaign on day 7' },
                { icon: RefreshCw, t: es ? 'Pedido repetido al día 9' : 'Repeat order on day 9' },
              ].map((s, i) => (
                <motion.div
                  key={s.t}
                  initial={{ opacity: 0, x: -10 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.2 + i * 0.15 }}
                  className="flex items-center gap-3 rounded-xl bg-white/[0.05] border border-white/10 px-3.5 py-2.5"
                >
                  <s.icon className="w-4 h-4 text-[#F77F11] shrink-0" />
                  <span className="text-[12.5px] text-neutral-300">{s.t}</span>
                </motion.div>
              ))}
            </div>
            <p className="mt-4 text-[11px] text-emerald-300 flex items-center gap-1"><TrendingUp className="w-3 h-3" /> {es ? '+24% pedidos recurrentes en 90 días' : '+24% repeat orders in 90 days'}</p>
          </Panel>
        </Grow>
      </div>
    </Shell>
  );
}

/* ── ONLINE REPUTATION — before / after + urgent situations ── */
function ReputationShowcase({ lang }) {
  const es = lang === 'es';
  const situations = [
    { icon: AlertTriangle, t: es ? 'Una reseña negativa se está haciendo viral' : 'A negative review is going viral', s: es ? 'Respuesta + oferta de recuperación en <60 min' : 'Reply + recovery offer in <60 min' },
    { icon: Timer, t: es ? 'Llevas 3 meses sin responder reseñas' : "You haven't replied to reviews in 3 months", s: es ? 'Backlog completo respondido en tu voz en 7 días' : 'Full backlog answered in your voice within 7 days' },
    { icon: TrendingDown, t: es ? 'Tu rating lleva 2 trimestres cayendo' : 'Your rating has dropped for 2 quarters', s: es ? 'Flujo de reseñas nuevas de clientes felices' : 'New-review flow from your happiest guests' },
  ];
  return (
    <Shell
      eyebrow={es ? 'Antes / Después' : 'Before / After'}
      title={es ? 'Tu reputación, operada cada día.' : 'Your reputation, operated daily.'}
      body={
        es
          ? 'El 93% de los comensales lee reseñas antes de reservar. Esto es lo que cambia cuando alguien las trabaja de verdad.'
          : '93% of diners read reviews before booking. This is what changes when someone actually works them.'
      }
      testid="showcase-online-reputation"
      lang={lang}
      why={
        es
          ? 'Una décima menos de rating puede costarte mesas cada noche. Esto no espera al próximo trimestre.'
          : 'One tenth of a star can cost you tables every single night. This does not wait until next quarter.'
      }
    >
      <div className="grid md:grid-cols-2 gap-5">
        <Grow i={0}>
          <Panel className="h-full border-white/10">
            <div className="flex items-center justify-between">
              <PanelTag>{es ? 'Antes de Kitxens' : 'Before Kitxens'}</PanelTag>
              <span className="text-[10px] font-bold tracking-[0.15em] uppercase text-rose-300 bg-rose-500/10 border border-rose-400/30 rounded-full px-2.5 py-0.5">{es ? 'En riesgo' : 'At risk'}</span>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-4xl font-bold text-neutral-300 tabular-nums">3.9</span>
              <Stars n={3} />
            </div>
            <ul className="mt-4 space-y-2 text-[12.5px] text-neutral-400">
              <li className="flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-rose-400" /> {es ? '12% de reseñas respondidas' : '12% of reviews answered'}</li>
              <li className="flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-rose-400" /> {es ? 'Última foto subida hace 14 meses' : 'Last photo uploaded 14 months ago'}</li>
              <li className="flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-rose-400" /> {es ? 'Negativas sin respuesta en primera página' : 'Unanswered negatives on page one'}</li>
            </ul>
          </Panel>
        </Grow>
        <Grow i={1}>
          <Panel className="h-full border-[#F77F11]/40 shadow-[0_30px_90px_-40px_rgba(247,127,17,0.5)]">
            <div className="flex items-center justify-between">
              <PanelTag>{es ? 'Con Kitxens · mes 4' : 'With Kitxens · month 4'}</PanelTag>
              <span className="text-[10px] font-bold tracking-[0.15em] uppercase text-emerald-300 bg-emerald-500/10 border border-emerald-400/30 rounded-full px-2.5 py-0.5">{es ? 'Creciendo' : 'Growing'}</span>
            </div>
            <div className="flex items-center gap-3">
              <motion.span
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                transition={{ delay: 0.4 }}
                className="text-4xl font-bold text-white tabular-nums"
              >
                4.7
              </motion.span>
              <Stars n={5} />
            </div>
            <ul className="mt-4 space-y-2 text-[12.5px] text-neutral-300">
              <li className="flex items-center gap-2"><Check className="w-3.5 h-3.5 text-[#F77F11]" /> {es ? '95%+ respondidas, primera respuesta <4h' : '95%+ answered, first response <4h'}</li>
              <li className="flex items-center gap-2"><Check className="w-3.5 h-3.5 text-[#F77F11]" /> {es ? '4+ fotos nuevas cada mes' : '4+ fresh photos every month'}</li>
              <li className="flex items-center gap-2"><Check className="w-3.5 h-3.5 text-[#F77F11]" /> {es ? 'Negativas recuperadas en <60 minutos' : 'Negatives recovered in <60 minutes'}</li>
            </ul>
          </Panel>
        </Grow>
      </div>
      <div className="mt-8">
        <p className="text-[11px] tracking-[0.2em] uppercase font-bold text-neutral-500 mb-4">{es ? 'Situaciones que resolvemos hoy mismo' : 'Situations we solve today'}</p>
        <div className="grid sm:grid-cols-3 gap-4">
          {situations.map((s, i) => (
            <Grow key={s.t} i={i}>
              <Panel className="h-full">
                <s.icon className="w-5 h-5 text-[#F77F11]" />
                <p className="mt-3 text-[14px] font-semibold text-white leading-snug">{s.t}</p>
                <p className="mt-2 text-[12.5px] text-neutral-400 leading-relaxed">{s.s}</p>
              </Panel>
            </Grow>
          ))}
        </div>
      </div>
    </Shell>
  );
}

/* ── DELIVERY OPTIMIZATION — cut costs starting at the recipe ── */
function DeliveryCostShowcase({ lang }) {
  const es = lang === 'es';
  const ingredients = [
    { n: es ? 'Pollo marinado 180g' : 'Marinated chicken 180g', c: '$1.62' },
    { n: es ? 'Tortilla + arroz + frijol' : 'Tortilla + rice + beans', c: '$0.94' },
    { n: es ? 'Salsas y toppings' : 'Salsas & toppings', c: '$0.58' },
    { n: es ? 'Packaging delivery' : 'Delivery packaging', c: '$0.47' },
  ];
  return (
    <Shell
      eyebrow={es ? 'Rebajamos tus costes' : 'We cut your costs'}
      title={es ? 'La rentabilidad empieza en la receta.' : 'Profitability starts at the recipe.'}
      body={
        es
          ? 'Antes de optimizar rutas o comisiones, costeamos cada plato al céntimo. Solo así sabes qué vender en delivery — y a qué precio.'
          : 'Before optimizing routes or commissions, we cost every dish to the cent. Only then do you know what to sell on delivery — and at what price.'
      }
      testid="showcase-delivery-optimization"
      lang={lang}
      why={
        es
          ? 'Sin escandallo, cada pedido de delivery puede estar perdiéndote dinero sin que lo sepas.'
          : 'Without recipe costing, every delivery order could be losing you money without you knowing it.'
      }
    >
      <div className="grid md:grid-cols-3 gap-5">
        <Grow i={0}>
          <Panel className="h-full">
            <div className="flex items-center justify-between">
              <PanelTag>{es ? 'Escandallo · Burrito de pollo' : 'Recipe costing · Chicken burrito'}</PanelTag>
              <UtensilsCrossed className="w-4 h-4 text-[#F77F11]" />
            </div>
            <div className="space-y-2">
              {ingredients.map((ing, i) => (
                <motion.div
                  key={ing.n}
                  initial={{ opacity: 0, x: -8 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.15 + i * 0.1 }}
                  className="flex justify-between text-[12.5px] text-neutral-300"
                >
                  <span>{ing.n}</span><span className="tabular-nums text-neutral-400">{ing.c}</span>
                </motion.div>
              ))}
            </div>
            <div className="mt-3 pt-3 border-t border-white/10 flex items-center justify-between">
              <span className="text-[12.5px] text-neutral-400">{es ? 'Coste total / PVP $12.90' : 'Total cost / price $12.90'}</span>
              <span className="text-[13px] font-bold text-white tabular-nums">$3.61</span>
            </div>
            <div className="mt-3 flex gap-2">
              <span className="rounded-full bg-[#F77F11]/15 border border-[#F77F11]/40 text-[#ffb077] text-[11px] font-bold px-2.5 py-1">Food cost 28%</span>
              <span className="rounded-full bg-emerald-500/10 border border-emerald-400/30 text-emerald-300 text-[11px] font-bold px-2.5 py-1">{es ? 'Margen 72%' : 'Margin 72%'}</span>
            </div>
          </Panel>
        </Grow>
        <Grow i={1}>
          <Panel className="h-full">
            <PanelTag>{es ? 'Mix de canales optimizado' : 'Optimized channel mix'}</PanelTag>
            <div className="space-y-3">
              <div className="flex items-center gap-3 rounded-xl bg-white/[0.05] border border-white/10 px-3.5 py-2.5">
                <UberEatsMark /><DoorDashMark />
                <span className="text-[12px] text-neutral-300 flex-1 min-w-0">{es ? 'Solo platos de margen alto' : 'High-margin dishes only'}</span>
              </div>
              <div className="flex items-center gap-3 rounded-xl bg-[#F77F11]/10 border border-[#F77F11]/40 px-3.5 py-2.5">
                <Zap className="w-4 h-4 text-[#F77F11] shrink-0" />
                <span className="text-[12px] text-white flex-1 min-w-0">{es ? 'Clientes fieles → canal directo 0%' : 'Loyal guests → direct channel 0%'}</span>
              </div>
            </div>
            <p className="mt-5 text-[24px] font-bold text-white tabular-nums">$1,240<span className="text-[13px] text-neutral-400 font-medium">/{es ? 'mes en comisiones ahorradas' : 'mo saved in commissions'}</span></p>
          </Panel>
        </Grow>
        <Grow i={2}>
          <Panel className="h-full">
            <PanelTag>{es ? 'Operativa afinada' : 'Tuned operations'}</PanelTag>
            <div className="grid grid-cols-2 gap-2.5">
              {[
                { l: es ? 'Tiempo medio' : 'Avg. time', v: '24 min' },
                { l: es ? 'Reembolsos' : 'Refunds', v: '-18%' },
                { l: es ? 'Pedidos/hora pico' : 'Peak orders/hr', v: '+31%' },
                { l: es ? 'Rating delivery' : 'Delivery rating', v: '4.8★' },
              ].map((k, i) => (
                <motion.div
                  key={k.l}
                  initial={{ opacity: 0, scale: 0.94 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.15 + i * 0.08 }}
                  className="rounded-xl bg-white/[0.05] border border-white/10 px-3 py-2.5"
                >
                  <p className="text-[9.5px] tracking-[0.12em] uppercase font-bold text-neutral-500">{k.l}</p>
                  <p className="text-[16px] font-bold text-white tabular-nums">{k.v}</p>
                </motion.div>
              ))}
            </div>
            <p className="mt-4 text-[11px] text-neutral-500">{es ? 'Menú, zonas, horarios y packaging revisados cada mes.' : 'Menu, zones, hours and packaging reviewed monthly.'}</p>
          </Panel>
        </Grow>
      </div>
    </Shell>
  );
}

/* ── CUSTOMER MARKETING — organized, segmented, scheduled ── */
function CustomerMarketingShowcase({ lang }) {
  const es = lang === 'es';
  const segments = [
    { n: 'VIP', c: 214, tone: 'text-[#ffb077] bg-[#F77F11]/15 border-[#F77F11]/40' },
    { n: es ? 'Nuevos' : 'New', c: 96, tone: 'text-emerald-300 bg-emerald-500/10 border-emerald-400/30' },
    { n: es ? 'Dormidos' : 'Lapsed', c: 342, tone: 'text-neutral-300 bg-white/5 border-white/15' },
  ];
  const calendar = [
    { d: es ? 'Lun' : 'Mon', t: es ? 'Campaña VIP: menú degustación' : 'VIP campaign: tasting menu' },
    { d: es ? 'Jue' : 'Thu', t: es ? 'Recordatorio nuevos: 2ª visita -10%' : 'New-guest nudge: 2nd visit -10%' },
    { d: es ? 'Dom' : 'Sun', t: es ? 'Reactivación dormidos: te echamos de menos' : 'Lapsed win-back: we miss you' },
  ];
  return (
    <Shell
      eyebrow={es ? 'Marketing con sistema' : 'Marketing with a system'}
      title={es ? 'El marketing no lo es todo. Organizado, segmentado y programado — sí.' : "Marketing isn't everything. Organized, segmented and scheduled — it is."}
      body={
        es
          ? 'Enviar por enviar quema tu base de datos. Cada mensaje sale al segmento correcto, en el momento correcto, con un objetivo medible.'
          : 'Blasting burns your database. Every message goes to the right segment, at the right moment, with a measurable goal.'
      }
      testid="showcase-customer-marketing"
      lang={lang}
      why={
        es
          ? 'Conseguir un cliente nuevo cuesta 5× más que hacer volver a uno que ya te conoce.'
          : 'Winning a new guest costs 5× more than bringing back one who already knows you.'
      }
    >
      <div className="grid md:grid-cols-3 gap-5">
        <Grow i={0}>
          <Panel className="h-full">
            <PanelTag>{es ? '1 · Segmentado' : '1 · Segmented'}</PanelTag>
            <div className="space-y-2.5">
              {segments.map((s, i) => (
                <motion.div
                  key={s.n}
                  initial={{ opacity: 0, x: -10 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.15 + i * 0.1 }}
                  className="flex items-center justify-between rounded-xl bg-white/[0.05] border border-white/10 px-3.5 py-2.5"
                >
                  <span className="flex items-center gap-2 text-[13px] text-neutral-200"><Users className="w-3.5 h-3.5 text-[#F77F11]" /> {s.n}</span>
                  <span className={`text-[11px] font-bold rounded-full border px-2 py-0.5 tabular-nums ${s.tone}`}>{s.c}</span>
                </motion.div>
              ))}
            </div>
            <p className="mt-4 text-[11px] text-neutral-500">{es ? 'Alimentado por tu POS y tus pedidos directos.' : 'Fed by your POS and direct orders.'}</p>
          </Panel>
        </Grow>
        <Grow i={1}>
          <Panel className="h-full">
            <PanelTag>{es ? '2 · Programado' : '2 · Scheduled'}</PanelTag>
            <div className="space-y-2.5">
              {calendar.map((c, i) => (
                <motion.div
                  key={c.d}
                  initial={{ opacity: 0, y: 8 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.2 + i * 0.12 }}
                  className="flex items-center gap-3 rounded-xl bg-white/[0.05] border border-white/10 px-3.5 py-2.5"
                >
                  <span className="w-9 h-9 shrink-0 rounded-lg bg-[#F77F11]/15 border border-[#F77F11]/30 text-[#ffb077] text-[10px] font-bold flex items-center justify-center uppercase">{c.d}</span>
                  <span className="text-[12px] text-neutral-300 leading-snug">{c.t}</span>
                </motion.div>
              ))}
            </div>
            <p className="mt-4 text-[11px] text-neutral-500">{es ? 'Email, SMS y WhatsApp desde un solo calendario.' : 'Email, SMS and WhatsApp from one calendar.'}</p>
          </Panel>
        </Grow>
        <Grow i={2}>
          <Panel className="h-full">
            <PanelTag>{es ? '3 · Medido' : '3 · Measured'}</PanelTag>
            <div className="rounded-xl bg-white/[0.05] border border-white/10 p-4">
              <p className="text-[11px] text-neutral-400">{es ? 'Campaña "Dormidos" · marzo' : '"Lapsed" campaign · March'}</p>
              <p className="mt-2 text-[26px] font-bold text-white tabular-nums">18%</p>
              <p className="text-[12px] text-neutral-400">{es ? 'volvieron a pedir en 14 días' : 'ordered again within 14 days'}</p>
              <div className="mt-3 h-2 rounded-full bg-white/10 overflow-hidden">
                <motion.div initial={{ width: 0 }} whileInView={{ width: '18%' }} viewport={{ once: true }} transition={{ duration: 0.9, delay: 0.3 }} className="h-full bg-gradient-to-r from-[#F77F11] to-[#ffb077] rounded-full" />
              </div>
            </div>
            <p className="mt-4 text-[22px] font-bold text-emerald-300 tabular-nums flex items-center gap-1.5"><TrendingUp className="w-4 h-4" /> $2,340</p>
            <p className="text-[11px] text-neutral-500">{es ? 'ingresos atribuidos a una sola campaña' : 'revenue attributed to a single campaign'}</p>
          </Panel>
        </Grow>
      </div>
    </Shell>
  );
}

/* ── LOCAL SEO — rank grid before/after ── */
function LocalSeoShowcase({ lang }) {
  const es = lang === 'es';
  const before = [12, 9, 14, 11, 8, 13, 15, 10, 9, 12, 11, 14, 13, 9, 15, 12];
  const after = [1, 2, 1, 3, 1, 2, 4, 1, 2, 1, 3, 2, 1, 5, 2, 3];
  const cell = (n, good) => (
    <span
      className={`aspect-square rounded-lg flex items-center justify-center text-[12px] font-bold tabular-nums ${
        good
          ? n <= 3
            ? 'bg-[#F77F11] text-white shadow-[0_0_18px_-4px_rgba(247,127,17,0.8)]'
            : 'bg-[#F77F11]/25 text-[#ffb077]'
          : 'bg-white/[0.06] text-neutral-500'
      }`}
    >
      {n}
    </span>
  );
  return (
    <Shell
      eyebrow={es ? 'Antes / Después' : 'Before / After'}
      title={es ? 'Tu posición en el mapa, calle a calle.' : 'Your map ranking, street by street.'}
      body={
        es
          ? 'Así medimos el Local SEO de verdad: tu posición media en el Map Pack desde cada zona de tu ciudad — antes y después de operar tu visibilidad.'
          : 'This is how real Local SEO is measured: your average Map Pack position from every part of town — before and after we operate your visibility.'
      }
      testid="showcase-local-seo"
      lang={lang}
      why={
        es
          ? 'Si no estás en el top 3 del mapa, para la mayoría de tu barrio simplemente no existes.'
          : "If you're not in the map's top 3, for most of your neighborhood you simply don't exist."
      }
    >
      <div className="grid md:grid-cols-2 gap-5">
        <Grow i={0}>
          <Panel>
            <div className="flex items-center justify-between">
              <PanelTag>{es ? 'Antes · posición media 11.7' : 'Before · avg. position 11.7'}</PanelTag>
              <MapPin className="w-4 h-4 text-neutral-500" />
            </div>
            <div className="grid grid-cols-4 gap-2">{before.map((n, i) => <React.Fragment key={i}>{cell(n, false)}</React.Fragment>)}</div>
          </Panel>
        </Grow>
        <Grow i={1}>
          <Panel className="border-[#F77F11]/40 shadow-[0_30px_90px_-40px_rgba(247,127,17,0.5)]">
            <div className="flex items-center justify-between">
              <PanelTag>{es ? 'Mes 5 · posición media 2.1' : 'Month 5 · avg. position 2.1'}</PanelTag>
              <MapPin className="w-4 h-4 text-[#F77F11]" />
            </div>
            <div className="grid grid-cols-4 gap-2">{after.map((n, i) => <React.Fragment key={i}>{cell(n, true)}</React.Fragment>)}</div>
          </Panel>
        </Grow>
      </div>
      <p className="mt-5 text-[11px] text-neutral-500 text-center">{es ? 'Grid de ranking ilustrativo — el tuyo se mide con datos reales en la auditoría gratuita.' : 'Illustrative rank grid — yours is measured with real data in the free audit.'}</p>
    </Shell>
  );
}

/* ── POS MANAGEMENT — one change, every channel ── */
function PosManagementShowcase({ lang }) {
  const es = lang === 'es';
  const targets = [
    { icon: <ToastMark />, l: 'POS' },
    { icon: <Globe className="w-4 h-4 text-[#F77F11]" />, l: es ? 'Web + pedidos' : 'Web + ordering' },
    { icon: <UberEatsMark />, l: 'Delivery apps' },
    { icon: <GoogleMark />, l: 'Google' },
  ];
  return (
    <Shell
      eyebrow={es ? 'Tu día a día, sin fricción' : 'Your day-to-day, frictionless'}
      title={es ? 'Un cambio. Todos los canales. Cero errores.' : 'One change. Every channel. Zero mistakes.'}
      body={
        es
          ? 'Cambiar un precio o agotar un plato ya no es abrir cinco paneles distintos. Lo gestionamos una vez y se propaga a todo.'
          : "Changing a price or 86'ing a dish is no longer five different dashboards. We manage it once and it propagates everywhere."
      }
      testid="showcase-pos-management"
      lang={lang}
      why={
        es
          ? 'Cada menú desincronizado es un pedido cancelado y una reseña negativa esperando a ocurrir.'
          : 'Every out-of-sync menu is a cancelled order and a negative review waiting to happen.'
      }
    >
      <div className="grid md:grid-cols-3 gap-5">
        <Grow i={0}>
          <Panel className="h-full">
            <PanelTag>{es ? 'La acción' : 'The action'}</PanelTag>
            <div className="rounded-xl bg-white/[0.05] border border-white/10 p-4">
              <p className="text-[11px] text-neutral-400">{es ? 'Actualizar precio' : 'Update price'}</p>
              <p className="mt-1 text-[15px] text-white font-semibold">Margherita Pizza</p>
              <p className="mt-1 text-[13px] tabular-nums"><span className="text-neutral-500 line-through">$14.00</span> <span className="text-[#F77F11] font-bold ml-2">$16.00</span></p>
            </div>
            <div className="mt-3 rounded-xl bg-white/[0.05] border border-white/10 p-4">
              <p className="text-[11px] text-neutral-400">{es ? 'Agotar plato (86)' : "86 an item"}</p>
              <p className="mt-1 text-[13px] text-neutral-300">{es ? 'Ceviche — oculto en todos los menús al instante' : 'Ceviche — hidden on every menu instantly'}</p>
            </div>
          </Panel>
        </Grow>
        <Grow i={1}>
          <Panel className="h-full">
            <PanelTag>{es ? 'La propagación' : 'The propagation'}</PanelTag>
            <div className="space-y-2.5">
              {targets.map((tg, i) => (
                <motion.div
                  key={tg.l}
                  initial={{ opacity: 0, x: -10 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.25 + i * 0.18 }}
                  className="flex items-center gap-3 rounded-xl bg-white/[0.05] border border-white/10 px-3.5 py-2.5"
                >
                  {tg.icon}
                  <span className="text-[12.5px] text-neutral-300 flex-1 min-w-0">{tg.l}</span>
                  <motion.span
                    initial={{ scale: 0 }}
                    whileInView={{ scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.5 + i * 0.18, type: 'spring', stiffness: 400 }}
                    className="w-5 h-5 rounded-full bg-emerald-500/20 flex items-center justify-center"
                  >
                    <Check className="w-3 h-3 text-emerald-300" />
                  </motion.span>
                </motion.div>
              ))}
            </div>
            <p className="mt-3 text-[11px] text-neutral-500">{es ? 'Sincronizado en segundos, no en turnos.' : 'Synced in seconds, not in shifts.'}</p>
          </Panel>
        </Grow>
        <Grow i={2}>
          <Panel className="h-full">
            <PanelTag>{es ? 'La tranquilidad' : 'The peace of mind'}</PanelTag>
            <div className="space-y-3">
              <div className="flex items-center gap-3 rounded-xl bg-white/[0.05] border border-white/10 px-3.5 py-3">
                <ShieldCheck className="w-4 h-4 text-[#F77F11]" />
                <div>
                  <p className="text-[13px] text-white font-semibold">99.9% uptime</p>
                  <p className="text-[11px] text-neutral-500">{es ? 'Monitorización 24/7 de tu stack' : '24/7 monitoring of your stack'}</p>
                </div>
              </div>
              <div className="flex items-center gap-3 rounded-xl bg-white/[0.05] border border-white/10 px-3.5 py-3">
                <Timer className="w-4 h-4 text-[#F77F11]" />
                <div>
                  <p className="text-[13px] text-white font-semibold">{es ? '6+ horas/semana recuperadas' : '6+ hours/week recovered'}</p>
                  <p className="text-[11px] text-neutral-500">{es ? 'Sin dobles capturas ni menús desincronizados' : 'No double entry, no out-of-sync menus'}</p>
                </div>
              </div>
              <div className="flex items-center gap-3 rounded-xl bg-white/[0.05] border border-white/10 px-3.5 py-3">
                <Receipt className="w-4 h-4 text-[#F77F11]" />
                <div>
                  <p className="text-[13px] text-white font-semibold">{es ? 'Cierres y reportes diarios' : 'Daily closes & reports'}</p>
                  <p className="text-[11px] text-neutral-500">{es ? 'Ventas por canal en tu email cada mañana' : 'Sales by channel in your inbox every morning'}</p>
                </div>
              </div>
            </div>
          </Panel>
        </Grow>
      </div>
    </Shell>
  );
}

/* ── POS INTEGRATIONS — hub flow ── */
function PosIntegrationsShowcase({ lang }) {
  const es = lang === 'es';
  const sources = [
    { icon: <ToastMark />, l: 'Toast' },
    { icon: <SquareMark />, l: 'Square' },
    { icon: <CloverMark />, l: 'Clover' },
    { icon: <LightspeedMark />, l: 'Lightspeed' },
  ];
  const outputs = [
    { icon: Globe, l: es ? 'Web y pedidos directos' : 'Website & direct ordering' },
    { icon: BarChart3, l: es ? 'Analítica unificada' : 'Unified analytics' },
    { icon: Mail, l: es ? 'Marketing con datos reales' : 'Marketing on real data' },
  ];
  return (
    <Shell
      eyebrow={es ? 'Cómo fluye tu operación' : 'How your operation flows'}
      title={es ? 'Tu POS habla. Todo lo demás escucha.' : 'Your POS talks. Everything else listens.'}
      body={
        es
          ? 'Conectamos tu punto de venta con cada sistema que usas — sin cambiar de POS y sin migraciones dolorosas.'
          : 'We wire your point of sale into every system you use — without switching POS and without painful migrations.'
      }
      testid="showcase-pos-integrations"
      lang={lang}
      why={
        es
          ? 'Tus datos ya existen. Si no fluyen, cada decisión la estás tomando a ciegas.'
          : "Your data already exists. If it doesn't flow, every decision you make is a blind one."
      }
    >
      <div className="grid md:grid-cols-[1fr_auto_1fr] gap-6 items-center max-w-4xl mx-auto">
        <Grow i={0}>
          <div className="space-y-2.5">
            {sources.map((s) => (
              <div key={s.l} className="flex items-center gap-3 rounded-xl bg-white/[0.05] border border-white/10 px-4 py-3">
                {s.icon}
                <span className="text-[13px] text-neutral-200 font-medium">{s.l}</span>
                <motion.span
                  animate={{ opacity: [0.4, 1, 0.4] }}
                  transition={{ duration: 2, repeat: Infinity }}
                  className="ml-auto w-1.5 h-1.5 rounded-full bg-emerald-400"
                />
              </div>
            ))}
          </div>
        </Grow>
        <Grow i={1} className="flex md:flex-col items-center justify-center gap-2">
          <motion.div
            animate={{ scale: [1, 1.08, 1] }}
            transition={{ duration: 2.4, repeat: Infinity, ease: 'easeInOut' }}
            className="w-20 h-20 rounded-2xl bg-[#F77F11] flex items-center justify-center shadow-[0_0_50px_-8px_rgba(247,127,17,0.9)]"
          >
            <Zap className="w-8 h-8 text-white" />
          </motion.div>
          <p className="text-[10px] tracking-[0.2em] uppercase font-bold text-neutral-500">Kitxens Hub</p>
        </Grow>
        <Grow i={2}>
          <div className="space-y-2.5">
            {outputs.map((o) => (
              <div key={o.l} className="flex items-center gap-3 rounded-xl bg-[#F77F11]/10 border border-[#F77F11]/30 px-4 py-3">
                <o.icon className="w-4 h-4 text-[#F77F11] shrink-0" />
                <span className="text-[13px] text-neutral-100">{o.l}</span>
              </div>
            ))}
          </div>
        </Grow>
      </div>
      <p className="mt-8 text-[11px] text-neutral-500 text-center">{es ? '40+ integraciones soportadas · datos en tiempo real · tú sigues siendo dueño de todo' : '40+ supported integrations · real-time data · you keep ownership of everything'}</p>
    </Shell>
  );
}

/* ── REVENUE GROWTH — technical revenue engine ── */
function RevenueEngineShowcase({ lang }) {
  const es = lang === 'es';
  const mix = [
    { l: es ? 'Directo (web + pedidos)' : 'Direct (web + ordering)', w: 42, hot: true },
    { l: 'Delivery apps', w: 31 },
    { l: es ? 'Sala + teléfono' : 'Dine-in + phone', w: 27 },
  ];
  const plan = [
    { d: es ? 'Días 0-30' : 'Days 0-30', t: es ? 'Auditoría, escandallos y quick wins de precio' : 'Audit, recipe costing and pricing quick wins' },
    { d: es ? 'Días 31-60' : 'Days 31-60', t: es ? 'Canal directo + campañas segmentadas activas' : 'Direct channel + segmented campaigns live' },
    { d: es ? 'Días 61-90' : 'Days 61-90', t: es ? 'Optimización del mix y reporte de ROI' : 'Mix optimization and ROI report' },
  ];
  return (
    <Shell
      eyebrow={es ? 'El motor de ingresos' : 'The revenue engine'}
      title={es ? 'Así se ve vender más, en datos.' : 'This is what selling more looks like, in data.'}
      body={
        es
          ? 'Sin humo: cada palanca que activamos aparece en tu panel — mix de canales, ticket, recurrencia y margen — con un plan de 90 días.'
          : 'No fluff: every lever we pull shows up in your dashboard — channel mix, ticket, repeat rate and margin — on a 90-day plan.'
      }
      testid="showcase-revenue-growth"
      lang={lang}
      why={
        es
          ? 'Sin medir el mix de canales, puedes estar creciendo en ventas y cayendo en margen a la vez.'
          : 'Without measuring channel mix, you can grow sales and shrink margin at the very same time.'
      }
    >
      <div className="grid md:grid-cols-3 gap-5">
        <Grow i={0}>
          <Panel className="h-full">
            <PanelTag>{es ? 'Mix de canales · mes 3' : 'Channel mix · month 3'}</PanelTag>
            <div className="space-y-3.5">
              {mix.map((m, i) => (
                <div key={m.l}>
                  <div className="flex justify-between text-[12px] mb-1.5">
                    <span className={m.hot ? 'text-[#ffb077] font-semibold' : 'text-neutral-400'}>{m.l}</span>
                    <span className={`font-bold tabular-nums ${m.hot ? 'text-[#F77F11]' : 'text-neutral-400'}`}>{m.w}%</span>
                  </div>
                  <div className="h-2.5 rounded-full bg-white/10 overflow-hidden">
                    <motion.div
                      initial={{ width: 0 }}
                      whileInView={{ width: `${m.w}%` }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.8, delay: i * 0.12 }}
                      className={`h-full rounded-full ${m.hot ? 'bg-gradient-to-r from-[#F77F11] to-[#ffb077]' : 'bg-neutral-500'}`}
                    />
                  </div>
                </div>
              ))}
            </div>
            <p className="mt-4 text-[11px] text-neutral-500">{es ? 'El canal directo crece — y no paga comisión.' : 'The direct channel grows — and pays no commission.'}</p>
          </Panel>
        </Grow>
        <Grow i={1}>
          <Panel className="h-full">
            <PanelTag>{es ? 'Palancas activas' : 'Active levers'}</PanelTag>
            <div className="grid grid-cols-2 gap-2.5">
              {[
                { l: es ? 'Ticket medio' : 'Avg. ticket', v: '+9%' },
                { l: es ? 'Recurrencia' : 'Repeat rate', v: '+12%' },
                { l: es ? 'Margen' : 'Margin', v: '+6pt' },
                { l: es ? 'Pedidos' : 'Orders', v: '+24%' },
              ].map((k, i) => (
                <motion.div
                  key={k.l}
                  initial={{ opacity: 0, scale: 0.94 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.15 + i * 0.08 }}
                  className="rounded-xl bg-white/[0.05] border border-white/10 px-3 py-3"
                >
                  <p className="text-[9.5px] tracking-[0.12em] uppercase font-bold text-neutral-500">{k.l}</p>
                  <p className="text-[18px] font-bold text-emerald-300 tabular-nums flex items-center gap-1"><TrendingUp className="w-3.5 h-3.5" />{k.v}</p>
                </motion.div>
              ))}
            </div>
            <p className="mt-4 text-[11px] text-neutral-500">{es ? 'Medido contra tu línea base, no contra promedios.' : 'Measured against your baseline, not averages.'}</p>
          </Panel>
        </Grow>
        <Grow i={2}>
          <Panel className="h-full">
            <PanelTag>{es ? 'Plan de 90 días' : 'The 90-day plan'}</PanelTag>
            <div className="relative">
              <span aria-hidden="true" className="absolute left-[13px] top-2 bottom-2 w-px bg-gradient-to-b from-[#F77F11] to-transparent" />
              <div className="space-y-4">
                {plan.map((p, i) => (
                  <motion.div
                    key={p.d}
                    initial={{ opacity: 0, x: 10 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.2 + i * 0.12 }}
                    className="relative flex gap-3"
                  >
                    <span className="relative z-10 shrink-0 w-7 h-7 rounded-full bg-neutral-900 border border-[#F77F11]/60 text-[#F77F11] text-[10px] font-bold flex items-center justify-center">{i + 1}</span>
                    <div>
                      <p className="text-[10.5px] tracking-[0.16em] uppercase font-bold text-[#F77F11]">{p.d}</p>
                      <p className="mt-0.5 text-[12.5px] text-neutral-300 leading-snug">{p.t}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </Panel>
        </Grow>
      </div>
    </Shell>
  );
}

/* ── Revenue hero visual (replaces the generic hero photo on /restaurant-revenue-growth) ── */
export function RevenueHeroMock({ lang }) {
  const es = lang === 'es';
  return (
    <div className="rounded-3xl bg-neutral-950 border border-white/10 shadow-[0_30px_90px_-30px_rgba(247,127,17,0.4)] p-6 aspect-[4/3] flex flex-col justify-between overflow-hidden relative" data-testid="revenue-hero-mock">
      <div aria-hidden="true" className="absolute -top-20 -right-16 w-64 h-64 rounded-full blur-3xl opacity-20" style={{ background: 'radial-gradient(circle, #F77F11, transparent 70%)' }} />
      <div className="relative flex items-center justify-between">
        <p className="text-[10px] tracking-[0.2em] uppercase font-bold text-neutral-500">{es ? 'Panel de ingresos · en vivo' : 'Revenue panel · live'}</p>
        <span className="inline-flex items-center gap-1.5 text-[10px] font-bold text-emerald-300"><span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" /> LIVE</span>
      </div>
      <div className="relative flex items-end gap-2 h-[42%]">
        {[36, 44, 40, 55, 62, 58, 74, 88].map((h, i) => (
          <motion.span
            key={i}
            initial={{ height: 0 }}
            animate={{ height: `${h}%` }}
            transition={{ duration: 0.8, delay: 0.2 + i * 0.07, ease: 'easeOut' }}
            className={`flex-1 rounded-t ${i >= 6 ? 'bg-gradient-to-t from-[#F77F11] to-[#ffb077]' : 'bg-white/15'}`}
          />
        ))}
      </div>
      <div className="relative grid grid-cols-3 gap-2">
        {[
          { l: es ? 'Ingresos' : 'Revenue', v: '+18%' },
          { l: 'Ticket', v: '+9%' },
          { l: es ? 'Directo' : 'Direct', v: '42%' },
        ].map((k) => (
          <div key={k.l} className="rounded-lg bg-white/[0.06] border border-white/10 px-2.5 py-2">
            <p className="text-[9px] tracking-[0.1em] uppercase font-bold text-neutral-500">{k.l}</p>
            <p className="text-[15px] font-bold text-white tabular-nums">{k.v}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ── BUSINESS INTELLIGENCE — virtual CEO agent ── */
function BusinessIntelligenceShowcase({ lang }) {
  const es = lang === 'es';
  return (
    <Shell
      eyebrow="General Manager AI"
      title={es ? 'Tu General Manager AI. Online 24/7.' : 'Your General Manager AI. Online 24/7.'}
      body={
        es
          ? 'No es otro dashboard que nadie mira. Es un General Manager AI que analiza tus números cada mañana, detecta lo que importa y te lo cuenta como lo haría tu mejor gerente — con decisiones, no con gráficas.'
          : "It's not another dashboard nobody opens. It's a General Manager AI that analyzes your numbers every morning, spots what matters and briefs you like your best GM would — with decisions, not charts."
      }
      testid="showcase-business-intelligence"
      lang={lang}
      why={
        es
          ? 'Los datos que nadie lee no valen nada. Tu GM AI los convierte en decisiones cada mañana.'
          : 'Data nobody reads is worth nothing. Your GM AI turns it into decisions every single morning.'
      }
    >
      <div className="grid md:grid-cols-3 gap-5">
        <Grow i={0}>
          <Panel className="h-full">
            <div className="flex items-center justify-between">
              <PanelTag>{es ? 'Briefing diario · 8:00 AM' : 'Daily briefing · 8:00 AM'}</PanelTag>
              <span className="inline-flex items-center gap-1.5 text-[10px] font-bold text-emerald-300">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" /> LIVE
              </span>
            </div>
            <div className="flex items-center gap-2.5">
              <span className="w-9 h-9 rounded-full bg-[#F77F11] flex items-center justify-center shadow-[0_0_24px_-6px_rgba(247,127,17,0.9)]">
                <Sparkles className="w-4 h-4 text-white" />
              </span>
              <p className="text-[13px] font-bold text-white">Kitxens GM · AI <span className="ml-1 text-[10px] font-semibold text-emerald-300">{es ? 'en línea' : 'online'}</span></p>
            </div>
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3, duration: 0.5 }}
              className="mt-3 rounded-2xl rounded-tl-sm bg-white/[0.07] border border-white/10 px-4 py-3.5"
            >
              <p className="text-[12.5px] text-neutral-200 leading-relaxed">
                {es
                  ? 'Buenos días 👋 Ayer facturaste $4,890 (+12% vs. el mismo martes). Plato estrella: pollo al carbón, 38 uds. Atención: el food cost subió 1.8pt — te propongo renegociar el aguacate con tu proveedor.'
                  : 'Good morning 👋 Yesterday you billed $4,890 (+12% vs. the same Tuesday). Star dish: charcoal chicken, 38 units. Heads-up: food cost rose 1.8pt — I suggest renegotiating avocado with your supplier.'}
              </p>
            </motion.div>
            <p className="mt-3 text-[11px] text-neutral-500">
              {es ? 'En tu WhatsApp y tu email, cada mañana.' : 'In your WhatsApp and inbox, every morning.'}
            </p>
          </Panel>
        </Grow>
        <Grow i={1}>
          <Panel className="h-full flex flex-col">
            <div className="flex items-center justify-between">
              <PanelTag>{es ? 'Midiendo ahora' : 'Measuring now'}</PanelTag>
              <span className="inline-flex items-center gap-1.5 text-[10px] font-bold text-emerald-300">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" /> LIVE
              </span>
            </div>
            <div className="flex-1 flex items-end gap-2 min-h-[130px]">
              {[52, 68, 44, 80, 60, 90, 71].map((h, i) => (
                <motion.span
                  key={i}
                  animate={{ height: [`${Math.round(h * 0.45)}%`, `${h}%`, `${Math.round(h * 0.7)}%`] }}
                  transition={{ duration: 2.2 + (i % 3) * 0.6, repeat: Infinity, repeatType: 'mirror', ease: 'easeInOut', delay: i * 0.18 }}
                  className="flex-1 rounded-t-md bg-gradient-to-t from-[#F77F11] to-[#ffb077]"
                  style={{ opacity: 0.5 + (i % 3) * 0.2 }}
                />
              ))}
            </div>
            <p className="mt-3 text-[11px] text-neutral-500">
              {es
                ? 'Ventas · reseñas · búsquedas · delivery — analizadas en tiempo real.'
                : 'Sales · reviews · searches · delivery — analyzed in real time.'}
            </p>
          </Panel>
        </Grow>
        <Grow i={2}>
          <Panel className="h-full">
            <PanelTag>{es ? 'Alertas y decisiones' : 'Alerts & decisions'}</PanelTag>
            <div className="space-y-2.5">
              {[
                { icon: TrendingUp, tone: 'text-emerald-300 border-emerald-400/30 bg-emerald-500/10', t: es ? 'Ventas +12% esta semana — mantén el push del menú de temporada.' : 'Sales +12% this week — keep pushing the seasonal menu.' },
                { icon: AlertTriangle, tone: 'text-amber-300 border-amber-400/30 bg-amber-500/10', t: es ? 'Food cost +1.8pt — margen en riesgo en 4 platos.' : 'Food cost +1.8pt — margin at risk on 4 dishes.' },
                { icon: TrendingDown, tone: 'text-red-300 border-red-400/30 bg-red-500/10', t: es ? 'Un competidor bajó precios de delivery en tu zona.' : 'A competitor just cut delivery prices in your area.' },
              ].map((a, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, x: 10 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.2 + i * 0.14 }}
                  className={`flex items-start gap-2.5 rounded-xl border px-3.5 py-2.5 ${a.tone}`}
                >
                  <a.icon className="w-4 h-4 shrink-0 mt-0.5" />
                  <span className="text-[12px] text-neutral-200 leading-snug">{a.t}</span>
                </motion.div>
              ))}
            </div>
            <p className="mt-4 inline-flex items-center gap-1.5 rounded-full bg-[#F77F11]/15 border border-[#F77F11]/40 text-[#ffb077] text-[11px] font-bold px-3 py-1.5">
              <Zap className="w-3.5 h-3.5" /> {es ? '3 decisiones sugeridas hoy' : '3 suggested decisions today'}
            </p>
          </Panel>
        </Grow>
      </div>
    </Shell>
  );
}

const SHOWCASES = {
  'online-ordering': OnlineOrderingShowcase,
  'online-reputation': ReputationShowcase,
  'delivery-optimization': DeliveryCostShowcase,
  'customer-marketing': CustomerMarketingShowcase,
  'local-seo': LocalSeoShowcase,
  'pos-management': PosManagementShowcase,
  'pos-integrations': PosIntegrationsShowcase,
  'revenue-growth': RevenueEngineShowcase,
  'business-intelligence': BusinessIntelligenceShowcase,
  'ai-receptionist': RachelShowcase,
  'review-automation': AutomationShowcase,
  'custom-ai-automation': AutomationShowcase,
  'catering-growth': CateringPipelineShowcase,
  'performance-reporting': WeeklyReportShowcase,
};

export default function ServiceShowcase({ slug, lang }) {
  const Cmp = SHOWCASES[slug];
  if (!Cmp) return null;
  return <Cmp lang={lang} />;
}
